// src/pages/OwnerVehicleForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import carDetailsJson from "../data/carDetails.json";
import statesJson from "../data/States.json";
import {
  fetchOwnerProfile,
  saveOwnerVehicle,
  updateOwnerVehicle,
  uploadVehicleDocument,
  uploadVehiclePhoto,
  autocompleteVehicleLocation,
  resolveVehicleLatLng,
    reverseGeocodeVehicleLocation, 
} from "../api/ownerCarsApi";
import { toast } from "../lib/toast";

/* ---------- helpers ---------- */

const getColorName = (c) => (typeof c === "string" ? c : c?.name || "");
const brandKeys = Object.keys(carDetailsJson || {});

/* ---------- Component ---------- */

export default function OwnerVehicleForm() {
  const navigate = useNavigate();
  const { registrationNumber: regParam } = useParams();
  const location = useLocation();

  const existingVehicleFromState = location.state?.existingVehicle || null;
  const isEdit = !!(regParam || existingVehicleFromState);

  const [owner, setOwner] = useState(null);
  const [carModels, setCarModels] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedModel, setSelectedModel] = useState("");

  const [states, setStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [showStatesDropdown, setShowStatesDropdown] = useState(false);
  const [isStateInputFocused, setIsStateInputFocused] = useState(false);

  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [suggestionsBusy, setSuggestionsBusy] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [activeLocationIndex, setActiveLocationIndex] = useState(-1);

  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [busy, setBusy] = useState(false);
  const [booting, setBooting] = useState(true);
  const [formError, setFormError] = useState("");

  const bookingStateRef = useRef(null);
  const locationInputRef = useRef(null);
  const locationSearchTimeoutRef = useRef(null);
  const locationCloseTimeoutRef = useRef(null);

  // NEW: current location busy state
  const [geoBusy, setGeoBusy] = useState(false);

  // Registration pattern validation
  const [regError, setRegError] = useState("");

  const [formData, setFormData] = useState({
    registrationNumber: "",
    brand: "",
    model: "",
    type: "",
    seatingCapacity: "",
    fuelType: "",
    color: "",
    averagePrice: "",
    mileage: "",
    yearOfPurchase: "",
    vehicleLocation: "",
    latitude: "",
    longitude: "",
    openForBookingStates: [],
    ownerName: "",
    idOfOwner: "",
    ownerEmail: "",
    ownerPhoneNumber: "",
    ownerAddress: "",
  });

  const fieldClass =
    "block w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-sm text-slate-900 shadow-sm " +
    "focus:border-slate-900 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/5";

  const readOnlyFieldClass =
    "block w-full rounded-xl border border-slate-100 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-900";

  // keeping as-is (in case used later for uploads)
  // eslint-disable-next-line no-unused-vars
  const fileInputClass =
    "block w-full text-[11px] text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 " +
    "file:text-[11px] file:font-semibold file:text-white hover:file:bg-slate-800 cursor-pointer";

  /* ---------- Year of purchase options (last 15 years) ---------- */

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 15 }, (_, idx) => String(now - idx));
  }, []);

  const yearsToRender = useMemo(() => {
    const base = [...yearOptions];
    const existing = formData.yearOfPurchase;
    if (existing && !base.includes(existing)) base.push(existing);
    return base.sort((a, b) => Number(b) - Number(a));
  }, [yearOptions, formData.yearOfPurchase]);

  /* ---------- effects: boot owner + states ---------- */

  useEffect(() => {
    setStates(statesJson || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const ownerProfile = await fetchOwnerProfile();
        if (!ownerProfile?.id) throw new Error("Owner profile is missing id");
        setOwner(ownerProfile);

        setFormData((prev) => ({
          ...prev,
          ownerName: ownerProfile.name || "",
          idOfOwner: ownerProfile.id || "",
          ownerEmail: ownerProfile.email || "",
          ownerPhoneNumber: ownerProfile.phoneNumber || "",
          ownerAddress: ownerProfile.address || "",
        }));

        const v = existingVehicleFromState;
        if (v) {
          const {
            brand,
            model,
            fuelType,
            color,
            registrationNumber,
            type,
            seatingCapacity,
            averagePrice,
            mileage,
            yearOfPurchase,
            vehicleLocation,
            openForBookingStates,
            latitude,
            longitude,
          } = v;

          const selectedBrandDetails = carDetailsJson[brand] || null;
          const models = selectedBrandDetails?.cars?.map((car) => car.name) || [];
          setSelectedBrand(brand || "");
          setCarModels(models);
          setSelectedModel(model || "");

          const selectedCar =
            selectedBrandDetails?.cars?.find((c) => c.name === model) || null;

          const defaultFuel = fuelType
            ? fuelType
            : Array.isArray(selectedCar?.fuel)
            ? selectedCar.fuel[0]
            : "";

          const defaultColor =
            color ||
            (Array.isArray(selectedCar?.colors) &&
              selectedCar.colors.length > 0 &&
              getColorName(selectedCar.colors[0])) ||
            "";

          const statesArray = Array.isArray(openForBookingStates)
            ? openForBookingStates
            : openForBookingStates
            ? String(openForBookingStates)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [];

          setSelectedStates(statesArray);

          setFormData((prev) => ({
            ...prev,
            registrationNumber: registrationNumber || "",
            brand: brand || "",
            model: model || "",
            type: selectedCar?.type || type || "",
            seatingCapacity: selectedCar?.seating_capacity || seatingCapacity || "",
            fuelType: defaultFuel,
            color: defaultColor,
            averagePrice: averagePrice || selectedCar?.average_price || "",
            mileage: mileage || selectedCar?.mileage || "",
            yearOfPurchase: yearOfPurchase || "",
            vehicleLocation: vehicleLocation || "",
            latitude: latitude ?? "",
            longitude: longitude ?? "",
            openForBookingStates: statesArray,
          }));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error booting owner vehicle form:", e);
        setFormError("We couldn't load your owner profile. Please refresh the page.");
      } finally {
        setBooting(false);
      }
    })();
  }, [existingVehicleFromState]);

  /* ---------- outside click to close booking states ---------- */

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (bookingStateRef.current && !bookingStateRef.current.contains(e.target)) {
        setShowStatesDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (locationSearchTimeoutRef.current) clearTimeout(locationSearchTimeoutRef.current);
      if (locationCloseTimeoutRef.current) clearTimeout(locationCloseTimeoutRef.current);
    };
  }, []);

  /* ---------- derived ---------- */

  const filteredStates = useMemo(() => {
    const term = stateSearchTerm.toLowerCase().trim();
    if (!term) return states;
    return states.filter((s) => s.state_name.toLowerCase().includes(term));
  }, [states, stateSearchTerm]);

  const effectiveRegistrationNumber = isEdit
    ? existingVehicleFromState?.registrationNumber || regParam || ""
    : formData.registrationNumber;

  /* ---------- registration helpers ---------- */

  const normalizeRegForCheck = (value) =>
    (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

  const validateRegistrationLocal = (raw) => {
    const normalized = normalizeRegForCheck(raw);
    if (!normalized) {
      setRegError("");
      return { normalized, valid: false };
    }

    if (!/^[A-Z0-9]+$/.test(normalized) || normalized.length < 6 || normalized.length > 10) {
      setRegError(
        "Enter a valid registration (6–10 letters/numbers). Example: MH01AB1234 or 21BH0001AA."
      );
      return { normalized, valid: false };
    }

    setRegError("");
    return { normalized, valid: true };
  };

  /* ---------- handlers: brand / model ---------- */

  const handleBrandChange = (e) => {
    const brand = e.target.value;
    setSelectedBrand(brand || "");
    setSelectedModel("");
    const bucket = carDetailsJson[brand];
    const models = bucket?.cars?.map((car) => car.name) || [];
    setCarModels(models);

    setFormData((prev) => ({
      ...prev,
      brand,
      model: "",
      type: "",
      seatingCapacity: "",
      averagePrice: "",
      mileage: "",
      fuelType: "",
      color: "",
    }));
  };

  const handleModelChange = (e) => {
    const model = e.target.value;
    setSelectedModel(model);

    const bucket = carDetailsJson[selectedBrand];
    const car = bucket?.cars?.find((c) => c.name === model) || null;

    if (!car) {
      setFormData((prev) => ({ ...prev, model }));
      return;
    }

    const defaultFuel = Array.isArray(car.fuel) ? car.fuel[0] : "";
    const defaultColor =
      Array.isArray(car.colors) && car.colors.length > 0 ? getColorName(car.colors[0]) : "";

    setFormData((prev) => ({
      ...prev,
      model: car.name,
      type: car.type || "",
      seatingCapacity: car.seating_capacity || "",
      averagePrice: car.average_price || "",
      mileage: car.mileage || "",
      fuelType: defaultFuel,
      color: defaultColor,
    }));
  };

  /* ---------- NEW: current location (ADDRESS ONLY) ---------- */

  const extractAddressLabel = (resolved) => {
    if (!resolved) return "";
    if (typeof resolved === "string") return resolved.trim();
    return (
      resolved.formattedAddress ||
      resolved.formatted_address ||
      resolved.address ||
      resolved.label ||
      resolved.location ||
      resolved.displayName ||
      resolved.display_name ||
      ""
    ).trim();
  };

 const reverseGeocodeToAddress = async (lat, lng) => {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (!key) {
    // If key missing, we cannot convert GPS to address
    return "";
  }

  const url =
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${encodeURIComponent(
      `${lat},${lng}`
    )}&key=${encodeURIComponent(key)}`;

  const res = await fetch(url);
  if (!res.ok) return "";

  const data = await res.json();
  if (!data || data.status !== "OK") return "";

  const first = Array.isArray(data.results) ? data.results[0] : null;
  const formatted = first?.formatted_address || "";

  return String(formatted || "").trim();
};

 const handleUseCurrentLocation = async () => {
  const isSecure =
    window.isSecureContext ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  if (!isSecure) {
    toast.error("Current location needs HTTPS (or localhost).");
    return;
  }

  if (!("geolocation" in navigator)) {
    toast.error("Geolocation is not supported on this device/browser.");
    return;
  }

  setGeoBusy(true);
  setFormError("");

  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 15000,
      });
    });

    const lat = position?.coords?.latitude;
    const lng = position?.coords?.longitude;

    if (typeof lat !== "number" || typeof lng !== "number") {
      throw new Error("Invalid GPS coordinates received");
    }

    // ✅ MUST get address (no coordinate fallback)
   // ✅ backend reverse geocode (production safe)
const resp = await reverseGeocodeVehicleLocation(lat, lng);
const address = extractAddressLabel(resp?.address || resp?.formattedAddress || resp);

if (!address) {
  toast.error("Unable to convert GPS to address. Please try again.");
  return;
}


setFormData((prev) => ({
  ...prev,
  vehicleLocation: address,   // ✅ store address only
  latitude: String(lat),
  longitude: String(lng),
}));

    if (!address) {
      toast.error(
        "Unable to convert GPS to address. Check Google key / Geocoding API enabled."
      );
      return;
    }

    setFormData((prev) => ({
      ...prev,
      vehicleLocation: address,     // ✅ store address
      latitude: String(lat),        // keep coords for backend
      longitude: String(lng),
    }));

    setLocationSuggestions([]);
    toast.success("Current location captured.");
  } catch (err) {
    const code = err?.code;
    if (code === 1) toast.error("Location permission denied.");
    else if (code === 2) toast.error("Location unavailable. Try again.");
    else if (code === 3) toast.error("Location request timed out. Try again.");
    else toast.error("Unable to fetch current location.");

    // eslint-disable-next-line no-console
    console.error("Geolocation error:", err);
  } finally {
    setGeoBusy(false);
  }
};

  /* ---------- generic change + location autocomplete + reg handling ---------- */

  const handleChange = async (e) => {
    const { name } = e.target;
    let { value } = e.target;

    if (name === "registrationNumber" && !isEdit) {
      const cleaned = value.toUpperCase().replace(/[^A-Z0-9\s-]/g, "");
      setFormData((prev) => ({ ...prev, registrationNumber: cleaned }));
      validateRegistrationLocal(cleaned);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "vehicleLocation") {
      const val = value.trim();
      setShowLocationSuggestions(true);
      setActiveLocationIndex(-1);

      if (locationSearchTimeoutRef.current) clearTimeout(locationSearchTimeoutRef.current);

      if (!val) {
        setLocationSuggestions([]);
        setSuggestionsBusy(false);
        setShowLocationSuggestions(false);
        setFormData((prev) => ({ ...prev, latitude: "", longitude: "" }));
        return;
      }

      locationSearchTimeoutRef.current = setTimeout(async () => {
        try {
          setSuggestionsBusy(true);
          const preds = await autocompleteVehicleLocation(val);
          setLocationSuggestions(preds);
          setActiveLocationIndex(preds.length ? 0 : -1);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Location autocomplete failed:", err);
          setLocationSuggestions([]);
        } finally {
          setSuggestionsBusy(false);
        }
      }, 220);
    }
  };

  const handleSelectSuggestion = async (sugg) => {
    const locationLabel = sugg?.fullText || sugg?.description || "";
    try {
      const coords = await resolveVehicleLatLng(locationLabel);
      setFormData((prev) => ({
        ...prev,
        vehicleLocation: locationLabel,
        latitude: coords?.latitude ?? "",
        longitude: coords?.longitude ?? "",
      }));
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      setActiveLocationIndex(-1);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error resolving lat/lng:", err);
      setFormData((prev) => ({
        ...prev,
        vehicleLocation: locationLabel,
      }));
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      setActiveLocationIndex(-1);
    }
  };

  const handleLocationFocus = () => {
    setShowLocationSuggestions(true);
    window.requestAnimationFrame(() => {
      locationInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  const handleLocationBlur = () => {
    if (locationCloseTimeoutRef.current) clearTimeout(locationCloseTimeoutRef.current);
    locationCloseTimeoutRef.current = setTimeout(() => {
      setShowLocationSuggestions(false);
      setActiveLocationIndex(-1);
    }, 140);
  };

  const handleLocationKeyDown = (e) => {
    if (!showLocationSuggestions || !locationSuggestions.length) {
      if (e.key === "ArrowDown" && formData.vehicleLocation.trim().length >= 2) {
        setShowLocationSuggestions(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveLocationIndex((idx) =>
        idx < locationSuggestions.length - 1 ? idx + 1 : 0
      );
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveLocationIndex((idx) =>
        idx > 0 ? idx - 1 : locationSuggestions.length - 1
      );
      return;
    }

    if (e.key === "Enter" && activeLocationIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(locationSuggestions[activeLocationIndex]);
      return;
    }

    if (e.key === "Escape") {
      setShowLocationSuggestions(false);
      setActiveLocationIndex(-1);
    }
  };

  /* ---------- booking states ---------- */

  const toggleState = (stateName) => {
    setSelectedStates((prev) => {
      let next;
      if (prev.includes(stateName)) next = prev.filter((s) => s !== stateName);
      else next = [...prev, stateName];

      setFormData((data) => ({
        ...data,
        openForBookingStates: next,
      }));
      return next;
    });
  };

  /* ---------- files ---------- */

  const handlePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotos((prev) => [...prev, file]);
  };

  const handleDocumentUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setDocuments((prev) => [...prev, file]);
  };

  /* ---------- submit ---------- */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.idOfOwner) {
      setFormError("Owner profile is missing. Please sign in again and retry.");
      toast.error("Owner profile missing – please sign in again.");
      return;
    }

    // OPTIONAL HARD RULE:
    // If you want to strictly require address (not empty) before submit, uncomment:
    // if (!formData.vehicleLocation?.trim()) {
    //   setFormError("Please set car location (address) using search or GPS icon.");
    //   toast.error("Car location is required.");
    //   return;
    // }

    let regForPayload = effectiveRegistrationNumber;

    if (!isEdit) {
      const { normalized, valid } = validateRegistrationLocal(formData.registrationNumber);
      if (!normalized || !valid) {
        setFormError("Please enter a valid registration number.");
        toast.error("Registration number looks invalid.");
        return;
      }
      regForPayload = normalized;
    }

    if (!regForPayload) {
      setFormError("Please enter vehicle registration number.");
      toast.error("Registration number is required.");
      return;
    }

    const openForBookingStatesString = (formData.openForBookingStates || []).join(",");

    const payload = {
      ...formData,
      registrationNumber: regForPayload,
      openForBookingStates: openForBookingStatesString,
    };

    setBusy(true);
    try {
      let vehicle;
      if (isEdit) {
        vehicle = await updateOwnerVehicle(regForPayload, payload);
        toast.success("Vehicle details updated.");
      } else {
        vehicle = await saveOwnerVehicle(payload);
        toast.success("Vehicle added.");
      }

      const reg = vehicle?.registrationNumber || regForPayload;

      for (const doc of documents) {
        try {
          await uploadVehicleDocument(reg, doc);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Document upload failed:", err);
        }
      }

      for (const photo of photos) {
        try {
          await uploadVehiclePhoto(reg, photo);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Photo upload failed:", err);
        }
      }

      navigate("/owner/cars");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Vehicle save failed:", err);
      setFormError(err?.response?.data || "We couldn't save this vehicle. Please try again.");
      toast.error("Unable to save vehicle. Please check the form.");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => navigate("/owner/cars");

  /* ---------- render ---------- */

  if (booting) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
        <Header />
        <main className="flex-1">
          <div className="mx-auto max-w-[1560px] px-4 py-12 sm:px-6 lg:px-10 2xl:px-12">
            <p className="text-sm text-slate-600">Loading vehicle form…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      <Header />

      <main className="flex-1">
        <div className="mx-auto max-w-[1560px] px-4 py-6 sm:px-6 lg:px-10 2xl:px-12">
          {/* Top bar */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Fleet console
              </p>
              <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                {isEdit ? "Edit vehicle" : "Add a new vehicle"}
              </h1>
              {owner && (
                <p className="mt-2 text-[11px] font-medium text-slate-500">
                  Under owner{" "}
                  <span className="font-semibold text-slate-800">
                    {owner.name || owner.email}
                  </span>
                  .
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-xs sm:text-sm font-semibold text-slate-700 shadow-sm hover:border-slate-900 hover:bg-slate-900 hover:text-white"
              >
                Back to cars
              </button>
            </div>
          </div>

          {/* Pink info strip */}
          <div className="mt-4 rounded-2xl border border-pink-100 bg-gradient-to-r from-[#FF2D83]/10 via-white to-white px-4 py-3 sm:px-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#FF2D83]">
                  Partner with Wayroot
                </p>
                <p className="mt-1 text-xs sm:text-sm text-slate-700">
                  List your cars on Wayroot and grow your revenue with{" "}
                  <span className="font-semibold">structured, scheduled bookings</span>. Keep it simple:
                </p>
                <p className="mt-1 text-xs sm:text-sm text-slate-700">
                  <span className="font-semibold">Step 1:</span> Add{" "}
                  <span className="font-semibold">vehicle details</span> &nbsp;·&nbsp;
                  <span className="font-semibold">Step 2:</span> Complete{" "}
                  <span className="font-semibold">vehicle documents</span> to go live in search.
                </p>
              </div>
              <ul className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-600 sm:mt-0">
                <li className="inline-flex items-center gap-1 rounded-full bg-white/80 px-3 py-1 shadow-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#FF2D83]" />
                  Step 1 · Vehicle details
                </li>
                <li className="inline-flex items-center gap-1 rounded-full bg-white/80 px-3 py-1 shadow-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#FF2D83]" />
                  Step 2 · Vehicle documents
                </li>
              </ul>
            </div>
          </div>

          {formError && (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-xs sm:text-sm text-red-700">
              {formError}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="mt-6 rounded-[26px] border border-slate-100 bg-white/95 px-4 py-6 shadow-[0_18px_45px_rgba(15,23,42,0.06)] sm:px-8 sm:py-8 lg:px-10 lg:py-9 pb-28 md:pb-12"
          >
            <div className="mb-5 flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Vehicle details
              </p>
              {effectiveRegistrationNumber && (
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-[11px] text-slate-600">
                  <span className="uppercase tracking-[0.14em] text-slate-400">Reg</span>
                  <span className="h-1 w-1 rounded-full bg-slate-300" />
                  <span className="tabular-nums font-semibold text-slate-800">
                    {effectiveRegistrationNumber}
                  </span>
                </div>
              )}
            </div>

            <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
              {/* Brand */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold tracking-wide text-slate-800">Brand</label>
                <select
                  name="brand"
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  className={fieldClass}
                >
                  <option value="">Select brand</option>
                  {brandKeys.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>

              {/* Model */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold tracking-wide text-slate-800">Model</label>
                <select
                  name="model"
                  value={selectedModel}
                  onChange={handleModelChange}
                  className={fieldClass}
                >
                  <option value="">Select model</option>
                  {carModels.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {/* Registration */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold tracking-wide text-slate-800">
                  Registration number
                </label>
                <input
                  type="text"
                  name="registrationNumber"
                  value={effectiveRegistrationNumber}
                  onChange={handleChange}
                  disabled={isEdit}
                  className={
                    isEdit
                      ? `${readOnlyFieldClass} disabled:bg-slate-100 disabled:text-slate-500`
                      : fieldClass
                  }
                  placeholder="e.g. MH01AB1234 or 21BH0001AA"
                />
                {!isEdit && (
                  <>
                    {!regError ? (
                      <p className="mt-1 text-[11px] text-slate-500">
                        Only letters and numbers are stored. Spaces/dashes are ignored.
                      </p>
                    ) : (
                      <p className="mt-1 text-[11px] text-red-600">{regError}</p>
                    )}
                  </>
                )}
              </div>

              {/* Fuel */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold tracking-wide text-slate-800">
                  Fuel type
                </label>
                <select
                  name="fuelType"
                  value={formData.fuelType}
                  onChange={handleChange}
                  className={fieldClass}
                >
                  <option value="">Select fuel</option>
                  {selectedModel &&
                    (carDetailsJson[selectedBrand]?.cars.find((car) => car.name === selectedModel)
                      ?.fuel || []
                    ).map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                </select>
              </div>

              {/* Color */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold tracking-wide text-slate-800">Color</label>
                <select
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  className={fieldClass}
                >
                  <option value="">Select color</option>
                  {selectedModel &&
                    (carDetailsJson[selectedBrand]?.cars.find((car) => car.name === selectedModel)
                      ?.colors || []
                    ).map((c, idx) => {
                      const name = getColorName(c);
                      return (
                        <option key={idx} value={name}>
                          {name}
                        </option>
                      );
                    })}
                </select>
              </div>

              {/* Year */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold tracking-wide text-slate-800">
                  Year of purchase
                </label>
                <select
                  name="yearOfPurchase"
                  value={formData.yearOfPurchase || ""}
                  onChange={handleChange}
                  className={fieldClass}
                >
                  <option value="">Select year</option>
                  {yearsToRender.map((yr) => (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  ))}
                </select>
              </div>

              {/* Car location + autocomplete + current location */}
              <div className="space-y-1.5 relative">
                <label className="text-[11px] font-semibold tracking-wide text-slate-800">
                  Car location
                </label>

                <div className="relative">
                  <input
                    ref={locationInputRef}
                    type="text"
                    name="vehicleLocation"
                    value={formData.vehicleLocation}
                    onChange={handleChange}
                    onFocus={handleLocationFocus}
                    onBlur={handleLocationBlur}
                    onKeyDown={handleLocationKeyDown}
                    className={`${fieldClass} pr-11`}
                    placeholder="Search area, city, or landmark"
                    autoComplete="off"
                  />

                  <button
                    type="button"
                    onClick={handleUseCurrentLocation}
                    disabled={geoBusy}
                    title="Use current location"
                    className={
                      "absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center " +
                      "rounded-xl border border-slate-200 bg-white shadow-sm hover:bg-slate-50 " +
                      "disabled:opacity-60 disabled:cursor-not-allowed"
                    }
                  >
                    {!geoBusy ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M12 2v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M12 19v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M2 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M19 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="2" />
                        <circle cx="12" cy="12" r="2" fill="currentColor" />
                      </svg>
                    ) : (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
                    )}
                  </button>
                </div>

                {showLocationSuggestions && formData.vehicleLocation.trim().length >= 2 && (
                  <ul
                    className="absolute z-20 mt-1 w-full overflow-auto rounded-xl border border-slate-200 bg-white text-xs shadow-lg"
                    style={{ maxHeight: "min(18rem, calc(100vh - 220px))" }}
                  >
                    {!suggestionsBusy && locationSuggestions.length === 0 && (
                      <li className="px-3 py-2 text-slate-400">No suggestions found.</li>
                    )}
                    {locationSuggestions.map((s) => (
                      <li
                        key={s.placeId || s.place_id || s.fullText || s.description}
                        className={`cursor-pointer px-3 py-2 ${
                          s === locationSuggestions[activeLocationIndex]
                            ? "bg-slate-50"
                            : "hover:bg-slate-50"
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectSuggestion(s);
                        }}
                      >
                        <div className="font-medium text-slate-800">{s.primaryText || s.fullText || s.description}</div>
                        {(s.secondaryText) ? <div className="mt-0.5 text-[11px] text-slate-500">{s.secondaryText}</div> : null}
                      </li>
                    ))}
                    {suggestionsBusy && (
                      <li className="px-3 py-2 text-slate-400">Fetching suggestions…</li>
                    )}
                  </ul>
                )}

                <p className="text-[11px] text-slate-500">
                  Tip: Click the GPS icon to auto-fill from your current location.
                </p>
              </div>

              {/* Booking states */}
              <div className="space-y-1.5 relative" ref={bookingStateRef}>
                <label className="text-[11px] font-semibold tracking-wide text-slate-800">
                  Booking states
                </label>
                <input
                  type="text"
                  className={fieldClass}
                  placeholder="Search & select states…"
                  value={isStateInputFocused ? stateSearchTerm : selectedStates.join(", ")}
                  onFocus={() => {
                    setIsStateInputFocused(true);
                    setShowStatesDropdown(true);
                  }}
                  onBlur={() => setIsStateInputFocused(false)}
                  onChange={(e) => setStateSearchTerm(e.target.value)}
                />

                {showStatesDropdown && (
                  <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-slate-200 bg-white text-xs shadow-lg">
                    {filteredStates.length > 0 ? (
                      filteredStates.map((state) => (
                        <li
                          key={state.state_id}
                          className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-slate-50"
                          onMouseDown={() => toggleState(state.state_name)}
                        >
                          <input
                            type="checkbox"
                            readOnly
                            checked={selectedStates.includes(state.state_name)}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-slate-900"
                          />
                          <span>{state.state_name}</span>
                        </li>
                      ))
                    ) : (
                      <li className="px-3 py-2 text-slate-400">No matching states</li>
                    )}
                  </ul>
                )}

                {selectedStates.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {selectedStates.map((st) => (
                      <span
                        key={st}
                        className="inline-flex items-center rounded-full bg-slate-50 px-2.5 py-1 text-[10px] font-medium text-slate-700"
                      >
                        {st}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-9 flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-xs sm:text-sm font-semibold text-slate-700 shadow-sm hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-6 text-xs sm:text-sm font-semibold text-white shadow-[0_10px_28px_rgba(15,23,42,0.5)] disabled:opacity-70"
              >
                {busy ? (isEdit ? "Saving…" : "Submitting…") : isEdit ? "Save changes" : "Add vehicle"}
              </button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}
